import { useEffect, useMemo, useState } from 'react'

import { UserSettingsUiTextRatioCalculation } from '@api'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import type { DateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import { Card } from '@/components/ui/card.tsx'
import { getFormattedDateRange } from '@/components/ui/export'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { statisticsService } from '@/modules/statistics/onlyfans/service/statistics-service.ts'
import { handleError } from '@/utils/error'
import { classNames, formatNumbersForSheet } from '@/utils/helpers'

import { mapDetailedStatisticsData } from '../../utils'
import { tableColumnTitles } from './consts'
import { getDetailedStatisticsColumns } from './detailedStatisticsColumns'

interface Props {
  creatorId: number | undefined
  date: DateRange
  showCents?: boolean
}

export const DetailedStatistics = ({ creatorId, date, showCents }: Props) => {
  const [isOneDay, setIsOneDay] = useState<boolean>(false)
  const { userSettings } = useAccount()

  useEffect(() => {
    if (!date) return
    setIsOneDay(date.from?.getTime() === date.to?.getTime())
  }, [date])

  const { data, isFetching, refetch } = useQuery(
    statisticsService.getStatisticsKey([creatorId ?? 0], date.from!, date.to!),
    () => {
      return statisticsService.getCreatorStats(
        [creatorId!],
        date.from || new Date(),
        date.to || new Date(),
      )
    },
    {
      enabled: !!date && !!creatorId,
      onError: (error) =>
        handleError(error, 'Failed to get detailed statistics', refetch),
    },
  )

  const useAllSubsRevenue =
    userSettings?.textRatioCalculation ===
    UserSettingsUiTextRatioCalculation.USE_ALL_SUBS_REVENUE

  const tableData = useMemo(
    () => mapDetailedStatisticsData(data, isOneDay, useAllSubsRevenue) ?? [],
    [data, isOneDay, useAllSubsRevenue],
  )

  const getTableSheetData = () =>
    tableData.map((item) => ({
      [tableColumnTitles.date]: format(
        parse(item.date, 'MMM dd, yyyy', new Date()),
        'yyyy-MM-dd',
      ),
      [tableColumnTitles.totalRevenue]: formatNumbersForSheet(
        item.totalRevenue,
      ),
      [tableColumnTitles.newSubs]: item.newSubs,
      [tableColumnTitles.newSubscriptionsRevenue]: formatNumbersForSheet(
        item.newSubscriptionsRevenue,
      ),
      [tableColumnTitles.recurringSubscriptionsRevenue]: formatNumbersForSheet(
        item.recurringSubscriptionsRevenue,
      ),
      [tableColumnTitles.tipsRevenue]: formatNumbersForSheet(item.tipsRevenue),
      [tableColumnTitles.messageRevenue]: formatNumbersForSheet(
        item.messageRevenue,
      ),
      [tableColumnTitles.textingRatio]: formatNumbersForSheet(
        item.textingRatio,
      ),
      [tableColumnTitles.openChats]: item.openChats,
      [tableColumnTitles.sellingChats]: item.sellingChats,
      [tableColumnTitles.averageFanSpend]: formatNumbersForSheet(
        item.averageFanSpend,
      ),
      [tableColumnTitles.linkClicks]: item.linkClicks,
      [tableColumnTitles.conversionRate]: formatNumbersForSheet(
        item.conversionRate,
      ),
    }))

  if (isFetching) {
    return (
      <>
        <h2 className="mb-4 text-2xl font-semibold">Detailed Statistics</h2>
        <Card className="p-6">
          <Skeleton className="h-40 w-full" />
        </Card>
      </>
    )
  }

  return (
    <DataTable
      title="Detailed Statistics"
      data={tableData}
      columns={getDetailedStatisticsColumns(showCents)}
      enablePagination
      enableSorting
      className={classNames({ hidden: isFetching })}
      enableExport
      exportOptions={{
        fileName: `OnlyFans Statistics, ${getFormattedDateRange(date)}`,
        getTableSheetData,
      }}
    />
  )
}
